import styled from '@emotion/styled';
import React from 'react';
import ReactPlayer from 'react-player';
import { QuizContent } from './QuizContent';
import { useStudent } from '../../context/StudentContext';
import { chPerm, sortObjects } from 'src/components/helpers';
import { getCourseProgress } from '../../utils';
import { PDFReader } from '../../../../components/PDFreader/pdfReader';
import {
  MediaController,
  MediaControlBar,
  MediaTimeRange,
  MediaTimeDisplay,
  MediaVolumeRange,
  MediaPlaybackRateButton,
  MediaPlayButton,
  MediaSeekBackwardButton,
  MediaSeekForwardButton,
  MediaMuteButton,
  MediaFullscreenButton,
} from 'media-chrome/react';
import LinkContent from './LinkContent';
import { screenSizes } from 'src/assets/styles/Literals';
import queryString from 'query-string';
import { CommentsSection } from './Comments';

// Real device detection (UA / touch tablet) — not viewport width
const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || navigator.vendor || '';
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    return true;
  }

  // iPadOS 13+ reports as MacIntel but is a touch tablet
  const isTouchMac =
    navigator.platform === 'MacIntel' && typeof navigator.maxTouchPoints === 'number'
      ? navigator.maxTouchPoints > 1
      : false;

  return isTouchMac;
};

const LessonContentWrapper = styled.div`
  .content-container {
    width: 100%;
    max-width: ${screenSizes.desktop};
    margin: 0 auto;
    padding: 0 1rem;

    @media (max-width: ${screenSizes.tablet}) {
      padding: 0 0.5rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      padding: 0 0.25rem;
    }
  }

  .video-container {
    position: relative;
    margin-bottom: 2rem;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

    > div {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    @media (max-width: ${screenSizes.tablet}) {
      margin-bottom: 1.5rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      margin-bottom: 1rem;
    }
  }

  .title {
    font-family: 'Raleway', sans-serif;
    margin-bottom: 2rem;
    color: #2c3e50;
    font-size: 2rem;
    line-height: 1.3;

    @media (max-width: ${screenSizes.tablet}) {
      font-size: 1.75rem;
      margin-bottom: 1.5rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      font-size: 1.5rem;
      margin-bottom: 1rem;
    }
  }

  .text-content {
    line-height: 1.6;
    margin-bottom: 2rem;
    font-size: 1rem;

    p {
      margin-bottom: 1rem;
    }

    img {
      max-width: 100%;
      height: auto;
      margin: 1rem 0;
      border-radius: 4px;
    }

    @media (max-width: ${screenSizes.tablet}) {
      font-size: 0.95rem;
      margin-bottom: 1.5rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      font-size: 0.9rem;
      margin-bottom: 1rem;
    }
  }

  .download-section {
    margin: 2rem 0;

    h3 {
      font-family: 'Raleway', sans-serif;
      color: #2c3e50;
      margin-bottom: 1rem;
      font-size: 1.25rem;

      @media (max-width: ${screenSizes.tablet}) {
        font-size: 1.1rem;
      }

      @media (max-width: ${screenSizes.mobile}) {
        font-size: 1rem;
      }
    }

    @media (max-width: ${screenSizes.tablet}) {
      margin: 1.5rem 0;
    }

    @media (max-width: ${screenSizes.mobile}) {
      margin: 1rem 0;
    }
  }

  .download-list {
    list-style: none;
    padding: 0;
    margin: 1rem 0;
  }

  .download-item {
    margin-bottom: 0.75rem;

    @media (max-width: ${screenSizes.mobile}) {
      margin-bottom: 0.5rem;
    }
  }

  .download-link {
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem;
    background-color: #f8f9fa;
    border-radius: 6px;
    transition: all 0.2s ease;
    color: #2c3e50;
    font-size: 0.95rem;

    &:hover {
      background-color: #e9ecef;
      text-decoration: none;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    @media (max-width: ${screenSizes.tablet}) {
      padding: 0.6rem;
      font-size: 0.9rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      padding: 0.5rem;
      font-size: 0.85rem;
      gap: 0.4rem;
    }
  }

  .navigation {
    display: flex;
    justify-content: center;
    margin-top: 2rem;
    padding: 2rem 0;
    border-top: 1px solid #e0e0e0;

    @media (max-width: ${screenSizes.tablet}) {
      margin-top: 1.5rem;
      padding: 1.5rem 0;
    }

    @media (max-width: ${screenSizes.mobile}) {
      margin-top: 1rem;
      padding: 1rem 0;
    }
  }

  .styled-button {
    padding: 0.8rem 1.5rem;
    border: none;
    border-radius: 6px;
    color: white;
    cursor: pointer;
    font-family: 'Raleway', sans-serif;
    transition: all 0.2s ease;
    background-color: #4caf50;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1rem;
    font-weight: 500;
    min-width: 200px;
    justify-content: center;

    &:hover {
      background-color: #45a049;
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(76, 175, 80, 0.3);
    }

    &:disabled {
      cursor: not-allowed;
      background-color: #cccccc;
      transform: none;
      box-shadow: none;
    }

    @media (max-width: ${screenSizes.tablet}) {
      padding: 0.7rem 1.2rem;
      font-size: 0.95rem;
      min-width: 180px;
    }

    @media (max-width: ${screenSizes.mobile}) {
      padding: 0.6rem 1rem;
      font-size: 0.9rem;
      min-width: 160px;
      gap: 0.4rem;
    }
  }

  .play-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(0, 0, 0, 0.7);
    color: white;
    border: none;
    border-radius: 50%;
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.8);
      transform: translate(-50%, -50%) scale(1.1);
    }

    svg {
      width: 30px;
      height: 30px;
      margin-left: 5px;
    }

    @media (max-width: ${screenSizes.mobile}) {
      width: 60px;
      height: 60px;

      svg {
        width: 25px;
        height: 25px;
        margin-left: 3px;
      }
    }
  }

  .video-wrapper {
    position: relative;
    display: inline-block;
    width: 100%;

    /* Mobile-specific video improvements */
    @media (max-width: ${screenSizes.mobile}) {
      /* Ensure proper touch handling */
      touch-action: manipulation;

      /* Improve video container for mobile */
      .react-player {
        border-radius: 8px;
        overflow: hidden;
      }

      /* Better play button visibility on mobile */
      .play-overlay {
        background: rgba(0, 0, 0, 0.8);
        backdrop-filter: blur(2px);

        &:active {
          transform: translate(-50%, -50%) scale(0.95);
        }
      }
    }
  }
`;

const LessonContent = (props) => {
  const { lesson, onComplete, course, onLessonSelect, guest } = props;
  const { student } = useStudent();
  const courseProgress = getCourseProgress(student, course.id);
  const isCompleted = courseProgress?.completedLessons?.includes(lesson.id);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isMuted, setIsMuted] = React.useState(true);
  const playerRef = React.useRef(null);

  let query = window.location.search;
  let parsed = queryString.parse(query);

  // Handle mobile autoplay restrictions
  const handlePlay = () => {
    setIsPlaying(true);
    // On mobile, keep muted initially to comply with autoplay policies
    if (!isMobileDevice()) {
      setIsMuted(false);
    }
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  // Mobile-specific play handler that unmutes after user interaction
  const handleMobilePlay = () => {
    setIsPlaying(true);
    // Small delay to ensure video starts playing before unmuting
    setTimeout(() => {
      setIsMuted(false);
    }, 100);
  };

  const findNextLesson = () => {
    if (!course?.AcademyLessons) return null;

    const currentIndex = sortObjects(course.AcademyLessons, 'order').findIndex(
      (l) => l.id === lesson.id
    );
    if (currentIndex === -1) return null;

    const nextLesson = sortObjects(course.AcademyLessons, 'order')
      .slice(currentIndex + 1)
      .find((l) => !courseProgress?.completedLessons?.includes(l.id) && !l.locked);
    return nextLesson;
  };

  const handleContinue = () => {
    if (!isCompleted) {
      onComplete();
    }
    const nextLesson = findNextLesson();
    if (nextLesson) {
      onLessonSelect(nextLesson);
    }
  };

  const renderContent = () => {
    switch (lesson.type) {
      case 'video':
        // Use ReactPlayer directly on mobile devices or when nomc/academy permission
        const shouldUseReactPlayer = parsed.nomc || chPerm('academy') || isMobileDevice();

        return shouldUseReactPlayer ? (
          <div className="video-wrapper">
            <ReactPlayer
              ref={playerRef}
              src={lesson.link}
              controls={true}
              playing={isPlaying}
              muted={isMuted}
              onPlay={handlePlay}
              onPause={handlePause}
              style={{
                width: '100%',
                height: isMobileDevice() ? '40vh' : '48vh',
              }}
              playsInline
              config={{
                youtube: {
                  playerVars: {
                    autoplay: isPlaying ? 1 : 0,
                    mute: isMuted ? 1 : 0,
                    playsinline: 1,
                    controls: 1,
                    modestbranding: 1,
                    rel: 0,
                    showinfo: 0,
                    iv_load_policy: 3,
                    fs: 1,
                    cc_load_policy: 0,
                    start: 0,
                    end: 0,
                  },
                },
              }}
            />
            {!isPlaying && (
              <button
                className="play-overlay"
                onClick={isMobileDevice() ? handleMobilePlay : () => setIsPlaying(true)}
                aria-label="Play video"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
            )}
          </div>
        ) : (
          <div>
            <div className="video-wrapper">
              <MediaController
                style={{
                  width: '100%',
                  aspectRatio: '16/9',
                }}
              >
                <ReactPlayer
                  ref={playerRef}
                  slot="media"
                  src={lesson.link}
                  controls={false}
                  playing={isPlaying}
                  muted={isMuted}
                  onPlay={handlePlay}
                  onPause={handlePause}
                  style={{
                    width: '100%',
                    height: '100%',
                    '--controls': 'none',
                    pointerEvents: 'none',
                  }}
                  playsInline
                  config={{
                    youtube: {
                      playerVars: {
                        autoplay: isPlaying ? 1 : 0,
                        mute: isMuted ? 1 : 0,
                        playsinline: 1,
                        controls: 0,
                        modestbranding: 1,
                        rel: 0,
                        showinfo: 0,
                        iv_load_policy: 3,
                        fs: 1,
                        cc_load_policy: 0,
                        start: 0,
                        end: 0,
                      },
                    },
                  }}
                />
                <MediaControlBar>
                  <MediaPlayButton />
                  <MediaSeekBackwardButton seekOffset={10} />
                  <MediaSeekForwardButton seekOffset={10} />
                  <MediaTimeRange />
                  <MediaTimeDisplay showDuration />
                  <MediaMuteButton />
                  <MediaVolumeRange />
                  <MediaPlaybackRateButton />
                  <MediaFullscreenButton />
                </MediaControlBar>
              </MediaController>
              {!isPlaying && (
                <button
                  className="play-overlay"
                  onClick={isMobileDevice() ? handleMobilePlay : () => setIsPlaying(true)}
                  aria-label="Play video"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
              )}
            </div>
            {!!lesson.content && (
              <div className="text-content" dangerouslySetInnerHTML={{ __html: lesson.content }} />
            )}
          </div>
        );
      case 'quiz':
        return (
          <QuizContent quiz={lesson.quiz} onComplete={onComplete} handleContinue={handleContinue} />
        );
      case 'pdf':
        return (
          <div>
            {!!lesson.content && (
              <div className="text-content" dangerouslySetInnerHTML={{ __html: lesson.content }} />
            )}
            <PDFReader key={lesson.link || lesson.id} url={lesson.link} />
            {!!lesson.link && <LinkContent link={lesson.link} />}
          </div>
        );
      case 'link':
        return (
          <div>
            {!!lesson.content && (
              <div className="text-content" dangerouslySetInnerHTML={{ __html: lesson.content }} />
            )}
            <LinkContent link={lesson.link} textCTA="ABRIR" />
          </div>
        );
      default:
        return (
          <div className="text-content" dangerouslySetInnerHTML={{ __html: lesson.content }} />
        );
    }
  };

  return (
    <LessonContentWrapper>
      <div className="content-container">
        <h2 className="title">{lesson.title}</h2>
        {renderContent()}
        {lesson.downloads && lesson.downloads.length > 0 && (
          <div className="download-section">
            <h3>Materiales de Apoyo</h3>
            <ul className="download-list">
              {lesson.downloads.map((download, index) => (
                <li className="download-item" key={index}>
                  <a className="download-link" href={download.url} download>
                    📄 {download.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
        {lesson.allowComments && <CommentsSection lesson={lesson} guest={guest} />}
        {lesson.type !== 'quiz' && (
          <div className="navigation">
            <button className="styled-button" onClick={handleContinue}>
              {isCompleted ? 'Continuar al Siguiente →' : 'Marcar como Completado y Continuar →'}
            </button>
          </div>
        )}
      </div>
    </LessonContentWrapper>
  );
};

export { LessonContent };
